import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCalendarClock, LuChevronRight, LuTrophy, LuUsers } from "react-icons/lu";

import { pokerApi } from "../../api/pokerApi";
import { showToast } from "../../utils/toast/Toast";
import Badge from "../ui/Badge";

const AMBER = "#ff8f52";

function isScheduledVisible(tournament) {
  return ["open", "closed", "in_progress"].includes(tournament?.status);
}

function pickScheduledBannerTournament(tournaments) {
  const visible = tournaments.filter(isScheduledVisible);
  const registeredLive = visible.find(
    (tournament) => tournament.status === "in_progress" && tournament.is_registered
  );
  if (registeredLive) return registeredLive;

  const registeredUpcoming = visible.find(
    (tournament) => ["open", "closed"].includes(tournament.status) && tournament.is_registered
  );
  if (registeredUpcoming) return registeredUpcoming;

  return visible.find((tournament) => tournament.status === "open") || null;
}

function formatCountdown(targetDate, now) {
  if (!targetDate) return "Scheduling";
  const ms = new Date(targetDate).getTime() - now;
  if (ms <= 0) return "Starting soon";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)}m`;
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value || 0));
}

function ctaFor(tournament) {
  if (tournament.status === "in_progress") {
    if (tournament.is_registered && tournament.game_id) return "Join Table";
    return "View Status";
  }
  if (tournament.is_registered) return "Registered";
  return "Register";
}

export default function TournamentBanner({ enabled }) {
  const navigate = useNavigate();
  const [scheduledTournaments, setScheduledTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      // MTT tournaments are deliberately not featured in this banner slot yet
      // -- it's built around the legacy ScheduledPokerMatch fields (title,
      // scheduled_start, small_blind/big_blind, is_creator) and picking a
      // priority between two differently-shaped tournament types for one
      // banner slot is its own design decision, not made here. Was
      // previously fetching pokerApi.loadMttTournamentsForDashboard() and
      // silently discarding the result -- removed rather than left as a
      // pointless network call. See PokerTournamentsPage.jsx's MTT tab for
      // the real MTT list/CTA integration.
      const scheduled = await pokerApi.listTournaments();
      setScheduledTournaments(scheduled?.results || []);
    } catch {
      setScheduledTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!enabled) return undefined;
    const tick = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(tick);
  }, [enabled]);

  const tournament = useMemo(
    () => pickScheduledBannerTournament(scheduledTournaments),
    [scheduledTournaments]
  );

  if (!enabled || loading || !tournament) return null;

  const registeredCount = Number(tournament.registered_count || 0);
  const maxPlayers = Math.max(1, Number(tournament.max_players || 1));
  const progress = Math.min(100, Math.round((registeredCount / maxPlayers) * 100));
  const isLive = tournament.status === "in_progress";
  const cta = ctaFor(tournament);
  const ctaDisabled = cta === "Registered";

  const handleCta = async () => {
    if (ctaDisabled) return;
    if (cta === "Join Table" && tournament.game_id) {
      navigate(`/games/poker/${tournament.game_id}`);
      return;
    }
    if (cta === "View Status") {
      navigate("/tournaments?tab=scheduled");
      return;
    }
    setBusy(true);
    try {
      await pokerApi.registerTournament(tournament.id);
      showToast("success", "Registered.");
      await load();
    } catch (error) {
      showToast("error", error?.response?.data?.error || "Could not register.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="relative mb-6 overflow-hidden rounded-[28px] border px-4 py-4 shadow-card-soft sm:mb-8 sm:px-6 sm:py-5"
      style={{
        borderColor: "rgba(255,143,82,0.48)",
        background:
          "linear-gradient(135deg, rgba(255,143,82,0.19), rgba(255,255,255,0.045) 42%, rgba(3,6,7,0.68)), linear-gradient(90deg, rgba(255,143,82,0.08), transparent)",
      }}
    >
      <div
        className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full opacity-70 blur-3xl"
        style={{ background: "rgba(255,143,82,0.30)" }}
      />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 opacity-[0.10] [background-image:radial-gradient(circle,rgba(255,255,255,.8)_1px,transparent_1px)] [background-size:18px_18px]" />
      <div className="pointer-events-none absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border border-brand-amber/35 bg-background-app-panel" />
      <div className="pointer-events-none absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full border border-brand-amber/35 bg-background-app-panel" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="hidden h-20 w-20 shrink-0 place-items-center rounded-2xl border border-brand-amber/30 bg-brand-amber/10 text-brand-amber shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] sm:grid">
          <LuTrophy size={38} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant="warning" className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em]">
              Single Table
            </Badge>
            <Badge variant={isLive ? "success" : "warning"} className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em]">
              {isLive ? "Live" : "Registering"}
            </Badge>
            {/* This banner only ever features one scheduled table (see load()'s
                comment) -- these two links are how a user discovers there's a
                whole other tournament type without the banner itself trying
                to represent both at once. */}
            <span className="ml-auto flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-faint">
              <button
                type="button"
                onClick={() => navigate("/tournaments?tab=scheduled")}
                className="transition hover:text-brand-amber"
              >
                Single Tables →
              </button>
              <button
                type="button"
                onClick={() => navigate("/tournaments?tab=mtt")}
                className="transition hover:text-brand-amber"
              >
                Multi-Table →
              </button>
            </span>
          </div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-text-primary sm:text-2xl">
            {tournament.title}
          </h2>
          <div className="mt-3 grid gap-2 text-[11px] text-text-secondary sm:grid-cols-4">
            <BannerStat icon={<LuUsers size={14} />} label="Registered" value={`${registeredCount}/${maxPlayers}`} />
            <BannerStat label="Blinds" value={`${formatNumber(tournament.small_blind)}/${formatNumber(tournament.big_blind)}`} />
            <BannerStat label="Stack" value={formatNumber(tournament.starting_chips)} />
            <BannerStat icon={<LuCalendarClock size={14} />} label={isLive ? "Status" : "Starts in"} value={isLive ? "Live table" : formatCountdown(tournament.scheduled_start, now)} />
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-black/35">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: AMBER }}
            />
          </div>
        </div>
        <div className="grid gap-2 sm:w-36 sm:shrink-0">
          <div className="rounded-2xl border border-brand-amber/25 bg-black/25 px-3 py-2 text-center">
            <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-text-faint">
              {isLive ? "Status" : "Starts"}
            </div>
            <div className="font-mono text-lg font-bold text-brand-amber">
              {isLive ? "LIVE" : formatCountdown(tournament.scheduled_start, now)}
            </div>
          </div>
          <button
            type="button"
            onClick={handleCta}
            disabled={busy || ctaDisabled}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border px-5 text-sm font-bold text-background-app shadow-[0_18px_50px_rgba(255,143,82,0.18)] transition hover:translate-y-[-1px] hover:brightness-110 disabled:cursor-default disabled:opacity-70"
            style={{ background: AMBER, borderColor: "rgba(255,143,82,0.72)" }}
          >
            {cta}
            {!ctaDisabled ? <LuChevronRight size={16} /> : null}
          </button>
        </div>
      </div>
    </section>
  );
}

function BannerStat({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-950/35 px-3 py-2">
      <div className="mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-text-faint">
        <span className="text-brand-amber">{icon}</span>
        {label}
      </div>
      <div className="font-mono text-sm font-semibold text-text-primary">
        {value}
      </div>
    </div>
  );
}
